import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, QrCode, Trash2, Wifi, WifiOff, RefreshCw, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  connected: "bg-emerald-500",
  qr: "bg-amber-500",
  connecting: "bg-blue-500",
  disconnected: "bg-slate-400",
};
const STATUS_LABEL = {
  connected: "Online",
  qr: "Aguardando QR",
  connecting: "Conectando",
  disconnected: "Desconectado",
};

export default function ConnectionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [connections, setConnections] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [qrDialog, setQrDialog] = useState(null); // connection object
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/connections");
    setConnections(data);
  };

  useEffect(() => { load(); }, []);

  useLiveEvents((payload) => {
    if (payload.event === "connection_update") {
      setConnections((prev) => prev.map((c) => c.id === payload.data.id ? { ...c, ...payload.data } : c));
      setQrDialog((cur) => (cur && cur.id === payload.data.id ? { ...cur, ...payload.data } : cur));
    }
  });

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/connections", { name, department: department || null });
      setName(""); setDepartment(""); setOpenNew(false);
      toast.success("Conexão criada");
      load();
    } finally { setBusy(false); }
  };

  const start = async (c) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/connections/${c.id}/start`);
      setQrDialog({ ...c, ...data });
      toast.info("Escaneie o QR Code no aplicativo do WhatsApp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao iniciar");
    } finally { setBusy(false); }
  };

  const refreshStatus = async (c) => {
    const { data } = await api.get(`/connections/${c.id}/status`);
    setConnections((prev) => prev.map((x) => x.id === c.id ? { ...x, ...data } : x));
    if (qrDialog && qrDialog.id === c.id) setQrDialog({ ...qrDialog, ...data });
  };

  const remove = async (c) => {
    if (!window.confirm(`Remover conexão "${c.name}"?`)) return;
    await api.delete(`/connections/${c.id}`);
    toast.success("Conexão removida");
    load();
  };

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="heading text-3xl font-extrabold">Conexões WhatsApp</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vincule os números da sua empresa via QR Code — sem depender da API oficial da Meta.
            </p>
          </div>
          {isAdmin && (
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="connection-new-button">
                  <Plus className="w-4 h-4 mr-2" /> Nova Conexão
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nova conexão WhatsApp</DialogTitle>
                  <DialogDescription>Dê um nome para identificar essa linha e (opcional) um departamento.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nome da conexão</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Comercial" />
                  </div>
                  <div>
                    <Label>Departamento (opcional)</Label>
                    <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Comercial, Suporte, Financeiro..." />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">Criar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {connections.length === 0 && (
            <Card className="md:col-span-2 xl:col-span-3">
              <CardContent className="p-10 text-center">
                <Smartphone className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <div className="heading font-bold text-lg mb-1">Nenhuma conexão ainda</div>
                <p className="text-sm text-muted-foreground">
                  {isAdmin ? "Clique em \"Nova Conexão\" para vincular seu primeiro WhatsApp." : "Peça ao administrador para cadastrar uma conexão."}
                </p>
              </CardContent>
            </Card>
          )}

          {connections.map((c) => (
            <Card key={c.id} data-testid="whatsapp-connection-card" className="overflow-hidden">
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
                <div>
                  <CardTitle className="heading text-base">{c.name}</CardTitle>
                  <div className="text-xs text-muted-foreground mt-1">{c.department || "Sem departamento"}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`status-dot ${STATUS_COLORS[c.status] || "bg-slate-400"}`} />
                  <Badge variant="secondary" className="text-[10px]">{STATUS_LABEL[c.status] || c.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {c.phone ? (
                  <div className="text-sm mono flex items-center gap-2">
                    <Wifi className="w-4 h-4 text-emerald-600" /> +{c.phone}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <WifiOff className="w-4 h-4" /> Sem número vinculado
                  </div>
                )}
                {isAdmin && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" onClick={() => start(c)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
                      <QrCode className="w-4 h-4 mr-1" /> {c.status === "connected" ? "Reconectar" : "Conectar"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => refreshStatus(c)} data-testid="qr-code-reload-button">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={!!qrDialog} onOpenChange={(v) => { if (!v) setQrDialog(null); }}>
        <DialogContent data-testid="qr-code-scan-container">
          <DialogHeader>
            <DialogTitle>Vincular {qrDialog?.name}</DialogTitle>
            <DialogDescription>
              Abra o WhatsApp no celular → Menu → <b>Aparelhos conectados</b> → <b>Conectar um aparelho</b> e aponte para o código abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4 gap-3">
            {qrDialog?.status === "connected" ? (
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                  <Wifi className="w-8 h-8 text-emerald-600" />
                </div>
                <div className="heading font-bold text-lg">Conectado com sucesso</div>
                {qrDialog.phone && <div className="text-sm text-muted-foreground mono">+{qrDialog.phone}</div>}
              </div>
            ) : qrDialog?.qr_data_url ? (
              <img src={qrDialog.qr_data_url} alt="QR Code" className="w-64 h-64 border border-border rounded-xl" />
            ) : (
              <div className="w-64 h-64 border-2 border-dashed border-border rounded-xl flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            )}
            <div className="text-xs text-muted-foreground">Status: <b>{STATUS_LABEL[qrDialog?.status] || qrDialog?.status}</b></div>
            <Button variant="outline" size="sm" onClick={() => refreshStatus(qrDialog)}>
              <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
